import React, { useState } from "react";
import { List, Card, Avatar, Typography, Button, Row, Col, Modal, Form, Input, Dropdown, Menu, Space, Tag, Tabs, message } from "antd";
import { MoreOutlined, EditOutlined, DeleteOutlined, FileTextOutlined, EyeOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { editPatientNote, fetchPatientNotes, deletePatientNote } from "../../../redux/doctorSlice";
import { useMediaQuery } from 'react-responsive';

const { Text } = Typography;
const { TextArea } = Input;

const getProgressNotes = (note) => {
  return note.progressNotes || ['No progress notes available'];
};

const noteTypeColors = {
  'Subjective': {
    border: '#4A90E2',
    bg: '#F0F7FF',
    text: '#2C5282'
  },
  'Objective': {
    border: '#48BB78', 
    bg: '#F0FFF4',
    text: '#2F855A'
  },
  'Assessment': {
    border: '#ED8936', 
    bg: '#FFFAF0',
    text: '#C05621'
  },
  'Plan': {
    border: '#9F7AEA', 
    bg: '#FAF5FF',
    text: '#6B46C1'
  }
};

/**
 * NotesList Component
 * 
 * Features:
 * - Displays notes in two tabs: Completed Notes and Drafts
 * - Shows accurate count of all notes (not just visible ones)
 * - Allows toggling between draft and completed status using editPatientNote
 * - Pagination within each tab for better performance
 * - Mobile-responsive with appropriate buttons for each action
 * - Loading states and user feedback for all actions
 */
const NotesList = ({ notes, onViewMore }) => {
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isProgressNotesModalVisible, setIsProgressNotesModalVisible] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedProgressNote, setSelectedProgressNote] = useState(null);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [visibleNotes, setVisibleNotes] = useState(3);
  const [togglingDraftStatus, setTogglingDraftStatus] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1023 });

  // Separate notes into drafts and completed
  const draftNotes = notes.filter(note => note.isDraft);
  const completedNotes = notes.filter(note => !note.isDraft);

  // Paginate the notes for display - show only a subset initially for better performance
  const visibleDraftNotes = draftNotes.slice(0, visibleNotes);
  const visibleCompletedNotes = completedNotes.slice(0, visibleNotes);

  const handleViewMore = () => {
    setVisibleNotes(prev => prev + 3);
  };

  // Reset pagination when switching between tabs
  const handleTabChange = () => {
    setVisibleNotes(3);
  };

  const handleEdit = (note) => {
    setSelectedNote(note);
    const values = {};
    note.progressNotes.forEach(noteText => {
      const [type, content] = noteText.split(': ');
      switch(type) {
        case 'Subjective':
          values.subjective = content;
          break;
        case 'Objective':
          values.objective = content;
          break;
        case 'Assessment':
          values.assessment = content;
          break;
        case 'Plan':
          values.patientPlan = content;
          break;
        default:
          break;
      }
    });
    values.personalNote = note.personalNotes;
    form.setFieldsValue(values);
    setIsEditModalVisible(true);
  };

  const handleDelete = (note) => {
    console.log('Deleting note:', note);
    setSelectedNote(note);
    setIsDeleteModalVisible(true);
  };

  const handleViewProgressNotes = (note) => {
    setSelectedProgressNote(note);
    setIsProgressNotesModalVisible(true);
    // Reset expanded sections when opening a new note
    setExpandedSections({});
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleMarkAsDraft = async (note) => {
    if (togglingDraftStatus) return; // Prevent multiple clicks
    
    try {
      setTogglingDraftStatus(true);
      
      // Extract the note content from progressNotes
      const subjective = note.progressNotes.find(n => n.startsWith('Subjective: '))?.replace('Subjective: ', '') || '';
      const objective = note.progressNotes.find(n => n.startsWith('Objective: '))?.replace('Objective: ', '') || '';
      const assessment = note.progressNotes.find(n => n.startsWith('Assessment: '))?.replace('Assessment: ', '') || '';
      const patientPlan = note.progressNotes.find(n => n.startsWith('Plan: '))?.replace('Plan: ', '') || '';
      
      // Use editPatientNote with isDraft: true
      const result = await dispatch(editPatientNote({
        personalNote: note.personalNotes,
        subjective,
        objective,
        assessment,
        patientPlan,
        noteId: note.noteId,
        isDraft: true // Mark as draft
      })).unwrap();
      
      if (result) {
        message.success('Note marked as draft successfully');
        // Refresh the notes list
        const patient = JSON.parse(localStorage.getItem("patient")) || { userRef: "" };
        dispatch(fetchPatientNotes(patient.userRef));
      }
    } catch (error) {
      console.error('Failed to mark note as draft:', error);
      message.error('Failed to mark note as draft');
    } finally {
      setTogglingDraftStatus(false);
    }
  };

  const handleMarkAsCompleted = async (note) => {
    if (togglingDraftStatus) return; // Prevent multiple clicks
    
    try {
      setTogglingDraftStatus(true);
      
      // Extract the note content from progressNotes
      const subjective = note.progressNotes.find(n => n.startsWith('Subjective: '))?.replace('Subjective: ', '') || '';
      const objective = note.progressNotes.find(n => n.startsWith('Objective: '))?.replace('Objective: ', '') || '';
      const assessment = note.progressNotes.find(n => n.startsWith('Assessment: '))?.replace('Assessment: ', '') || '';
      const patientPlan = note.progressNotes.find(n => n.startsWith('Plan: '))?.replace('Plan: ', '') || '';
      
      // Use editPatientNote with isDraft: false
      const result = await dispatch(editPatientNote({
        personalNote: note.personalNotes,
        subjective,
        objective,
        assessment,
        patientPlan,
        noteId: note.noteId,
        isDraft: false // Mark as completed
      })).unwrap();
      
      if (result) {
        message.success('Note marked as completed successfully');
        // Refresh the notes list
        const patient = JSON.parse(localStorage.getItem("patient")) || { userRef: "" };
        dispatch(fetchPatientNotes(patient.userRef));
      }
    } catch (error) {
      console.error('Failed to mark note as completed:', error);
      message.error('Failed to mark note as completed');
    } finally {
      setTogglingDraftStatus(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      console.log('Selected note for deletion:', selectedNote);
      if (!selectedNote || !selectedNote.noteId) {
        console.error('No note selected or missing noteId');
        return;
      }
      
      const result = await dispatch(deletePatientNote(selectedNote.noteId)).unwrap();
      if (result) {
        setIsDeleteModalVisible(false);
        // Refresh the notes list
        const patient = JSON.parse(localStorage.getItem("patient")) || { userRef: "" };
        dispatch(fetchPatientNotes(patient.userRef));
      }
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      const result = await dispatch(editPatientNote({
        personalNote: values.personalNote,
        subjective: values.subjective,
        objective: values.objective,
        assessment: values.assessment,
        patientPlan: values.patientPlan,
        noteId: selectedNote.noteId,
        isDraft: selectedNote.isDraft // Preserve the current draft status
      })).unwrap();
      
      if (result) {
        setIsEditModalVisible(false);
        form.resetFields();
        // Refresh the notes list
        const patient = JSON.parse(localStorage.getItem("patient")) || { userRef: "" };
        dispatch(fetchPatientNotes(patient.userRef));
      }
    } catch (error) {
      console.error('Failed to edit note:', error);
    }
  };

  const menu = (note) => (
    <Menu>
      <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEdit(note)}>
        Edit Note
      </Menu.Item>
      {!note.isDraft && (
        <Menu.Item 
          key="markAsDraft" 
          icon={<FileTextOutlined />} 
          onClick={() => handleMarkAsDraft(note)}
          disabled={togglingDraftStatus}
        >
          {togglingDraftStatus ? 'Marking as Draft...' : 'Mark as Draft'}
        </Menu.Item>
      )}
      {note.isDraft && (
        <Menu.Item 
          key="markAsCompleted" 
          icon={<FileTextOutlined />} 
          onClick={() => handleMarkAsCompleted(note)}
          disabled={togglingDraftStatus}
        >
          {togglingDraftStatus ? 'Marking as Completed...' : 'Mark as Completed'}
        </Menu.Item>
      )}
      <Menu.Item key="delete" icon={<DeleteOutlined />} onClick={() => handleDelete(note)}>
        Delete Note
      </Menu.Item>
    </Menu>
  );

  const getResponsiveColumns = () => {
    if (isMobile) {
      return {
        profile: 24,
        apptType: 24,
        progressNotes: 24,
        actions: 24
      };
    } else if (isTablet) {
      return {
        profile: 8,
        apptType: 7,
        progressNotes: 8,
        actions: 1
      };
    }
    return {
      profile: 8,
      apptType: 7,
      progressNotes: 8,
      actions: 1
    };
  };

  const columns = getResponsiveColumns();

  const NotesListContent = ({ notesData, noteType }) => (
    <>
      <Row 
        gutter={[24, 0]} 
        style={{ 
          marginBottom: 0, 
          padding: '20px 24px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
          borderRadius: '8px 8px 0 0',
          borderBottom: '2px solid #e8ecf1'
        }} 
        className="header-row"
      >
        <Col xs={24} sm={columns.profile}>
          <Text strong style={{ fontSize: '13px', color: '#1a1a1a', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            Profile
          </Text>
        </Col>
        <Col xs={24} sm={columns.apptType}>
          <Text strong style={{ fontSize: '13px', color: '#1a1a1a', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            Appt Type
          </Text>
        </Col>
        <Col xs={24} sm={columns.progressNotes}>
          <Text strong style={{ fontSize: '13px', color: '#1a1a1a', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            Progress Notes
          </Text>
        </Col>
        <Col xs={24} sm={columns.actions}></Col>
      </Row>
      <List
        itemLayout="vertical"
        dataSource={notesData}
        renderItem={item => (
          <List.Item
            key={item.name + item.date}
            style={{ 
              padding: "24px 24px", 
              borderBottom: "1px solid #f0f2f5",
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'default'
            }}
            className="note-item"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#fafbfc';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <Row gutter={[24, 16]} align="middle" style={{ width: '100%' }}>
              <Col xs={24} sm={columns.profile}>
                <Space align="start" size="middle" style={{ width: '100%' }}>
                  <Avatar 
                    src={item.profileImage} 
                    size={52}
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      border: '3px solid #ffffff'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '6px' }}>
                      <Space>
                        <Text strong style={{ 
                          color: "#1890ff", 
                          fontSize: '16px',
                          fontWeight: 600,
                          letterSpacing: '-0.2px'
                        }}>
                          {item.name}
                        </Text>
                        {item.isDraft && (
                          <Tag 
                            color="warning" 
                            style={{ 
                              margin: 0,
                              borderRadius: '12px',
                              border: 'none',
                              fontSize: '11px',
                              fontWeight: 600,
                              padding: '2px 10px'
                            }}
                          >
                            Draft
                          </Tag>
                        )}
                      </Space>
                    </div>
                    <Text type="secondary" style={{ 
                      fontSize: '13px', 
                      display: 'block', 
                      marginBottom: '4px',
                      color: '#8c8c8c',
                      fontWeight: 500
                    }}>
                      {item.role}
                    </Text>
                    <Text type="secondary" style={{ 
                      fontSize: '12px', 
                      display: 'block',
                      color: '#bfbfbf'
                    }}>
                      {item.date}
                    </Text>
                  </div>
                </Space>
              </Col>
              <Col xs={24} sm={columns.apptType}>
                <Text style={{ 
                  fontSize: '14px', 
                  color: '#434343',
                  fontWeight: 500,
                  lineHeight: '1.5'
                }}>
                  {item.appointmentType || 'No Appointment Type'}
                </Text>
              </Col>
              <Col xs={24} sm={columns.progressNotes}>
                <Button
                  type="link"
                  icon={<EyeOutlined style={{ fontSize: '16px' }} />}
                  onClick={() => handleViewProgressNotes(item)}
                  style={{
                    color: '#1890ff',
                    padding: '4px 8px',
                    height: 'auto',
                    fontSize: '14px',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e6f7ff';
                    e.currentTarget.style.color = '#0050b3';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#1890ff';
                  }}
                >
                  View Notes
                </Button>
              </Col>
              <Col xs={24} sm={columns.actions} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                {isMobile && (
                  <Space>
                    <Button type="primary" onClick={() => handleEdit(item)}>Edit</Button>
                    {item.isDraft && (
                      <Button 
                        type="default" 
                        onClick={() => handleMarkAsCompleted(item)}
                        loading={togglingDraftStatus}
                        disabled={togglingDraftStatus}
                      >
                        Mark as Completed
                      </Button>
                    )}
                    {!item.isDraft && (
                      <Button 
                        type="default" 
                        onClick={() => handleMarkAsDraft(item)}
                        loading={togglingDraftStatus}
                        disabled={togglingDraftStatus}
                      >
                        Mark as Draft
                      </Button>
                    )}
                  </Space>
                )}
                {!isMobile && (
                  <Dropdown overlay={menu(item)} trigger={['click']}>
                    <MoreOutlined 
                      style={{ 
                        fontSize: '20px', 
                        cursor: 'pointer',
                        color: '#8c8c8c',
                        padding: '8px',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#1890ff';
                        e.currentTarget.style.background = '#e6f7ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#8c8c8c';
                        e.currentTarget.style.background = 'transparent';
                      }}
                    />
                  </Dropdown>
                )}
              </Col>
            </Row>
          </List.Item>
        )}
      />
      {noteType === 'drafts' && visibleNotes < draftNotes.length && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Button type="dashed" onClick={handleViewMore}>
            View More Drafts
          </Button>
        </div>
      )}
      {noteType === 'completed' && visibleNotes < completedNotes.length && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Button type="dashed" onClick={handleViewMore}>
            View More Completed Notes
          </Button>
        </div>
      )}
    </>
  );

  const items = [
    {
      key: '1',
      label: `Completed Notes (${completedNotes.length})`,
      children: <NotesListContent notesData={visibleCompletedNotes} noteType="completed" />,
    },
    {
      key: '2',
      label: `Drafts (${draftNotes.length})`,
      children: <NotesListContent notesData={visibleDraftNotes} noteType="drafts" />,
    },
  ];

  return (
    <>
    <Tabs 
    defaultActiveKey="1" 
    items={items}
    style={{ 
      marginBottom: 24,
    }}
    onTabChange={handleTabChange}
    tabBarStyle={{
      background: '#ffffff',
      padding: '0 24px',
      margin: 0,
      borderRadius: '12px 12px 0 0',
      borderBottom: '2px solid #e8ecf1'
    }}
    size="large"
  />
  {/* Removed the single View More Drafts/Completed Notes button */}
    <Card 
      bordered={false}
      style={{
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        background: '#ffffff',
        overflow: 'hidden'
      }}
    >
     
      
      <Modal
        title="Edit Note"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsEditModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        ]}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="subjective"
            label="Subjective"
            rules={[{ required: true, message: 'Please enter subjective notes' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Enter patient's reported symptoms and concerns"
            />
          </Form.Item>

          <Form.Item
            name="objective"
            label="Objective"
            rules={[{ required: true, message: 'Please enter objective findings' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Enter measurable observations and test results"
            />
          </Form.Item>

          <Form.Item
            name="assessment"
            label="Assessment"
            rules={[{ required: true, message: 'Please enter assessment' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Enter your professional assessment and diagnosis"
            />
          </Form.Item>

          <Form.Item
            name="patientPlan"
            label="Plan"
            rules={[{ required: true, message: 'Please enter treatment plan' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Enter treatment plan and next steps"
            />
          </Form.Item>

          <Form.Item
            name="personalNote"
            label="Personal Notes"
            rules={[{ required: true, message: 'Please enter personal notes' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Enter personal notes"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Delete Note"
        open={isDeleteModalVisible}
        onCancel={() => setIsDeleteModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsDeleteModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="delete" type="primary" danger onClick={handleDeleteConfirm}>
            Delete
          </Button>
        ]}
      >
        <p>Are you sure you want to delete this note? This action cannot be undone.</p>
      </Modal>

      <Modal
        title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            padding: '4px 0'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
                background: 'linear-gradient(135deg, #4A90E2 0%, #BA4AE2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
            }}>
              <EyeOutlined style={{ fontSize: '20px', color: '#ffffff' }} />
            </div>
            <span style={{ 
              fontSize: '20px', 
              fontWeight: 600,
              color: '#1a1a1a',
              letterSpacing: '-0.3px'
            }}>
              Progress Notes
            </span>
          </div>
        }
        open={isProgressNotesModalVisible}
        onCancel={() => setIsProgressNotesModalVisible(false)}
        footer={[
          <Button 
            key="close" 
            type="primary" 
            onClick={() => setIsProgressNotesModalVisible(false)}
            style={{
              borderRadius: '8px',
              height: '40px',
              padding: '0 24px',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
            }}
          >
            Close
          </Button>
        ]}
        width={isMobile ? '95%' : 900}
        style={{ top: 20 }}
        styles={{
          body: {
            padding: '24px',
            background: '#fafbfc',
            overflowY: 'auto'
          },
          content: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        {selectedProgressNote && (
          <div style={{ padding: '0' }}>
            <div style={{ 
              marginBottom: '28px', 
              padding: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              borderRadius: '12px',
              border: '1px solid #e8ecf1',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
            }}>
              <Space align="start" size="large">
                <Avatar 
                  src={selectedProgressNote.profileImage} 
                  size={64}
                  style={{
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    border: '4px solid #ffffff'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: '8px' }}>
                    <Space>
                      <Text strong style={{ 
                        fontSize: '18px', 
                        color: '#1890ff',
                        fontWeight: 700,
                        letterSpacing: '-0.3px'
                      }}>
                        {selectedProgressNote.name}
                      </Text>
                      {selectedProgressNote.isDraft && (
                        <Tag 
                          color="warning" 
                          style={{ 
                            marginLeft: '8px',
                            borderRadius: '12px',
                            border: 'none',
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '4px 12px'
                          }}
                        >
                          Draft
                        </Tag>
                      )}
                    </Space>
                  </div>
                  <Text type="secondary" style={{ 
                    fontSize: '14px', 
                    display: 'block', 
                    marginBottom: '6px',
                    color: '#8c8c8c',
                    fontWeight: 500
                  }}>
                    {selectedProgressNote.role}
                  </Text>
                  <Text type="secondary" style={{ 
                    fontSize: '12px', 
                    display: 'block',
                    marginBottom: '8px',
                    color: '#bfbfbf'
                  }}>
                    {selectedProgressNote.date}
                  </Text>
                  <div style={{
                    marginTop: '12px',
                    padding: '10px 14px',
                    background: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #e8ecf1'
                  }}>
                    <Text style={{ 
                      fontSize: '14px', 
                      color: '#434343',
                      fontWeight: 500
                    }}>
                      {selectedProgressNote.appointmentType || 'No Appointment Type'}
                    </Text>
                  </div>
                </div>
              </Space>
            </div>
            
            <div style={{ 
              overflowY: 'auto', 
              paddingRight: '8px',
              marginBottom: '24px'
            }}>
              {getProgressNotes(selectedProgressNote).map((note, idx) => {
                // Split only on the first occurrence of ': ' to preserve content with colons
                const colonIndex = note.indexOf(': ');
                const type = colonIndex !== -1 ? note.substring(0, colonIndex) : note;
                const content = colonIndex !== -1 ? note.substring(colonIndex + 2) : '';
                const colors = noteTypeColors[type] || {
                  border: '#335cad',
                  bg: '#f8f9fa',
                  text: '#335cad'
                };
                
                const sectionKey = `${selectedProgressNote?.noteId || 'note'}-${idx}`;
                const isExpanded = expandedSections[sectionKey] !== false; // Default to expanded
                
                return (
                  <div
                    key={idx}
                    style={{
                      marginBottom: '20px',
                      padding: '20px 24px',
                      background: colors.bg,
                      borderRadius: '12px',
                      borderLeft: `5px solid ${colors.border}`,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateX(4px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateX(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '5px',
                      height: '100%',
                      background: `linear-gradient(180deg, ${colors.border} 0%, ${colors.border}dd 100%)`
                    }} />
                    <div
                      onClick={() => toggleSection(sectionKey)}
                      style={{
                        cursor: 'pointer',
                        userSelect: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: isExpanded ? '14px' : '0',
                        paddingLeft: '12px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.8';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      <Text
                        style={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: colors.text,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          position: 'relative',
                          flex: 1
                        }}
                      >
                        {type}
                      </Text>
                      {isExpanded ? (
                        <UpOutlined style={{ fontSize: '12px', color: colors.text, marginLeft: '12px' }} />
                      ) : (
                        <DownOutlined style={{ fontSize: '12px', color: colors.text, marginLeft: '12px' }} />
                      )}
                    </div>
                    {isExpanded && (
                      <Text
                        style={{
                          fontSize: '15px',
                          color: '#1a1a1a',
                          lineHeight: '1.9',
                          wordBreak: 'break-word',
                          display: 'block',
                          whiteSpace: 'pre-wrap',
                          fontWeight: 400,
                          paddingLeft: '12px',
                          overflow: 'visible',
                          textOverflow: 'clip'
                        }}
                      >
                        {content || 'No content available'}
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ 
              marginTop: '28px', 
              padding: '20px 24px',
              background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
              borderRadius: '12px',
              border: '1px solid #e8ecf1',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
            }}>
              <Text strong style={{ 
                fontSize: '16px', 
                color: '#1a1a1a', 
                display: 'block', 
                marginBottom: '16px',
                fontWeight: 700,
                letterSpacing: '-0.3px'
              }}>
                Personal Notes
              </Text>
              <Text
                style={{
                  fontSize: '15px',
                  color: selectedProgressNote.personalNotes ? '#434343' : '#bfbfbf',
                  lineHeight: '1.9',
                  wordBreak: 'break-word',
                  display: 'block',
                  whiteSpace: 'pre-wrap',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  fontStyle: selectedProgressNote.personalNotes ? 'normal' : 'italic',
                  border: '1px solid #e8ecf1',
                  minHeight: '60px'
                }}
              >
                {selectedProgressNote.personalNotes || 'No personal notes'}
              </Text>
            </div>
          </div>
        )}
      </Modal>
    </Card>
    </>
  );
};

export default NotesList; 